import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '@/core/firebase';
import { resolvePostAuthDestination } from '@/core/workspaceBootstrap';
import { readReturnQuery } from '@/core/urlRouting';
import AuthLayout from '@/layouts/AuthLayout';
import { useAuth } from '@/contexts/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingReturn = readReturnQuery(searchParams.toString());

  const verifyRegistrationAndRoute = useCallback(async () => {
    if (!auth.currentUser) return;
    setVerifying(true);
    setError('');
    try {
      const destination = await resolvePostAuthDestination(pendingReturn);
      navigate(destination, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  }, [navigate, pendingReturn]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    void verifyRegistrationAndRoute();
  }, [authLoading, isAuthenticated, verifyRegistrationAndRoute]);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const confirmedEmail = email.trim() || window.prompt('Confirm your email for sign-in');
    if (!confirmedEmail) {
      setError('Email address is required to complete sign-in.');
      return;
    }

    setEmail(confirmedEmail);
    setError('');
    setEmailSent(false);
    setEmailSending(true);
    signInWithEmailLink(auth, confirmedEmail, window.location.href)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Email sign-in failed');
      })
      .finally(() => {
        setEmailSending(false);
      });
  }, [email]);

  async function handleEmailLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading || verifying || emailSending) {
      setError('Please wait for the current sign-in to finish.');
      return;
    }
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError('Please enter an email address.');
      return;
    }

    setError('');
    setEmailSent(false);
    setEmailSending(true);
    try {
      const loginContinue = pendingReturn
        ? `${window.location.origin}/login?next=${encodeURIComponent(pendingReturn)}`
        : `${window.location.origin}/login`;
      await sendSignInLinkToEmail(auth, normalizedEmail, {
        url: loginContinue,
        handleCodeInApp: true,
      });
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  }

  async function handleGoogleLogin() {
    if (verifying || emailSending) return;
    setLoading(true);
    setError('');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="aspectly:w-full aspectly:max-w-md aspectly:bg-surface-container-lowest aspectly:p-10 aspectly:rounded-xl aspectly:ambient-shadow aspectly:relative aspectly:overflow-hidden aspectly:-translate-y-8">
        <div className="aspectly:absolute aspectly:top-0 aspectly:left-0 aspectly:w-full aspectly:h-1 aspectly:bg-linear-to-r aspectly:from-primary aspectly:to-primary-dim" />

        <div className="aspectly:text-center aspectly:mb-8">
          <h1 className="aspectly:font-headline aspectly:text-3xl aspectly:font-extrabold aspectly:tracking-tight aspectly:text-on-surface aspectly:mb-2">
            Welcome Back
          </h1>
          <p className="aspectly:text-on-surface-variant aspectly:text-sm">
            Enter your email to receive a magic link to your workspace.
          </p>
        </div>

        {error && (
          <div className="aspectly:mb-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {error}
          </div>
        )}

        {emailSent && (
          <div className="aspectly:mb-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-primary-container aspectly:text-primary aspectly:text-sm">
            Magic link sent. Check your inbox to finish signing in.
          </div>
        )}

        <div className="aspectly:space-y-3 aspectly:mb-8">
          <button
            onClick={handleGoogleLogin}
            disabled={loading || verifying || emailSending}
            className="aspectly:w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:gap-3 aspectly:py-3 aspectly:px-4 aspectly:bg-surface-container-low aspectly:hover:bg-surface-container aspectly:transition-colors aspectly:rounded-xl aspectly:text-sm aspectly:font-medium aspectly:text-on-surface-variant aspectly:border aspectly:border-outline-variant/10 aspectly:cursor-pointer aspectly:disabled:opacity-60"
          >
            <svg className="aspectly:w-5 aspectly:h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.94.46 3.77 1.18 5.42l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {verifying ? 'Verifying workspace…' : loading ? 'Signing in…' : 'Continue with Google'}
          </button>
        </div>

        <div className="aspectly:flex aspectly:items-center aspectly:gap-4 aspectly:mb-8">
          <div className="aspectly:h-px aspectly:grow aspectly:bg-outline-variant/20" />
          <span className="aspectly:text-[10px] aspectly:font-bold aspectly:tracking-widest aspectly:text-on-surface-variant/60 aspectly:uppercase">
            Or Email Login
          </span>
          <div className="aspectly:h-px aspectly:grow aspectly:bg-outline-variant/20" />
        </div>

        <form className="aspectly:space-y-6" onSubmit={handleEmailLogin}>
          <div className="aspectly:space-y-2">
            <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:tracking-wider aspectly:text-on-surface-variant aspectly:uppercase aspectly:ml-1">
              Email Address
            </label>
            <div className="aspectly:relative aspectly:group">
              <span className="material-symbols-outlined aspectly:absolute aspectly:left-4 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant/50 aspectly:group-focus-within:text-primary aspectly:transition-colors">
                alternate_email
              </span>
              <input
                className="aspectly:w-full aspectly:pl-11 aspectly:pr-4 aspectly:py-3 aspectly:bg-surface-container-low aspectly:border-b-2 aspectly:border-transparent aspectly:focus:border-primary aspectly:focus:ring-0 aspectly:transition-all aspectly:rounded-t-lg aspectly:text-sm aspectly:text-on-surface aspectly:outline-hidden aspectly:placeholder:text-outline-variant"
                placeholder="name@company.com"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailSent) setEmailSent(false);
                }}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || verifying || emailSending}
            className="aspectly:w-full aspectly:py-4 aspectly:bg-linear-to-br aspectly:from-primary aspectly:to-primary-dim aspectly:text-on-primary aspectly:font-headline aspectly:font-bold aspectly:rounded-xl aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:hover:opacity-90 aspectly:active:scale-[0.98] aspectly:transition-all aspectly:tracking-wide aspectly:cursor-pointer aspectly:disabled:opacity-60"
          >
            {emailSending ? 'Sending link…' : 'Login'}
          </button>
        </form>

        <div className="aspectly:mt-8 aspectly:text-center">
          <p className="aspectly:text-sm aspectly:text-on-surface-variant">
            Don&apos;t have an account?{' '}
            <a className="aspectly:text-primary aspectly:font-semibold aspectly:hover:underline" href="#">
              Signup today!
            </a>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
